<template>
	<div class="my-task-page">
		<div class="page-header">
			<div class="page-title-group">
				<span class="page-title">我的任务</span>
				<!-- 查看模式提示 -->
				<span v-if="viewUser" class="view-user-tip">
					正在查看 <b>{{ viewUser }}</b> 的任务（仅查看）
					<i class="el-icon-close" @click="clearViewUser"></i>
				</span>
			</div>
			<div class="header-actions">
				<!-- 查看切换：本部门及下级部门人员任务（仅查看，数据来自基础设置人员表） -->
				<el-select v-model="viewUser" size="small" clearable filterable placeholder="查看他人任务" class="view-user-select"
					@change="onViewUserChange" popper-class="view-user-select-dropdown">
					<el-option v-for="u in viewUserOptions" :key="u.nickName" :value="u.nickName"
						:label="u.staffName + (u.nickName !== u.staffName ? '（' + u.nickName + '）' : '')">
						<span class="view-user-option">
							<span class="view-user-name">{{ u.staffName }}</span>
							<span class="view-user-meta">{{ u.deptName || '未分配部门' }}<template v-if="u.postName"> / {{ u.postName }}</template></span>
						</span>
					</el-option>
				</el-select>
				<el-input v-model="searchKey" placeholder="搜索任务号/任务名称" size="small" class="search-input" clearable>
					<i class="el-icon-search el-input__icon" slot="prefix"></i>
				</el-input>
				<!-- 按关联客户查询：输入客户名称或编码，回车/清空触发后端筛选 -->
				<el-input v-model="customerKey" placeholder="按客户名称/编码查询" size="small" class="customer-input" clearable
					@keyup.enter.native="onCustomerSearch" @clear="onCustomerSearch">
					<i class="el-icon-office-building el-input__icon" slot="prefix"></i>
				</el-input>
				<!-- 列设置：显示/隐藏、拖动顺序提示 -->
				<el-popover placement="bottom-end" width="220" trigger="click" popper-class="col-setting-pop">
					<div class="col-setting">
						<div class="col-setting-title">显示/隐藏栏目</div>
						<el-checkbox-group v-model="visibleProps" class="col-setting-group">
							<el-checkbox v-for="col in columns" :key="col.prop" :label="col.prop">{{ col.label }}</el-checkbox>
						</el-checkbox-group>
						<div class="col-setting-tip">提示：拖动表头可左右调整列顺序</div>
						<el-button type="text" size="small" @click="resetColumns">恢复默认</el-button>
					</div>
					<el-button slot="reference" size="small" icon="el-icon-setting">列设置</el-button>
				</el-popover>
				<el-button type="warning" icon="el-icon-data-analysis" size="small" @click="openTaskAnalysis">时效分析</el-button>
				<el-button v-if="!viewUser" type="primary" icon="el-icon-plus" size="small" @click="openCreateDialog()">新建任务</el-button>
			</div>
		</div>

		<!-- 页签：待处理 / 转办事项 / 已完成 / 已终止 / 定时任务 -->
		<el-tabs v-model="activeTab" class="task-tabs" @tab-click="onTabChange">
			<el-tab-pane name="pending">
				<span slot="label">待处理 <el-badge :value="tabCount.pending" :hidden="tabCount.pending === 0" class="tab-badge" type="danger" /></span>
			</el-tab-pane>
			<el-tab-pane name="transferred">
				<span slot="label">转办事项 <el-badge :value="tabCount.transferred" :hidden="tabCount.transferred === 0" class="tab-badge" type="warning" /></span>
			</el-tab-pane>
			<!-- 别人知会我的任务：只读关注，可查看处理过程、补充说明、发表建议 -->
			<el-tab-pane name="notified">
				<span slot="label">@知会我的 <el-badge :value="tabCount.notified" :hidden="tabCount.notified === 0" class="tab-badge" type="warning" /></span>
			</el-tab-pane>
			<el-tab-pane name="completed">
				<span slot="label">已完成 <el-badge :value="tabCount.completed" :hidden="tabCount.completed === 0" class="tab-badge" type="success" /></span>
			</el-tab-pane>
			<el-tab-pane name="discarded">
				<span slot="label">已终止 <el-badge :value="tabCount.discarded" :hidden="tabCount.discarded === 0" class="tab-badge" type="info" /></span>
			</el-tab-pane>
			<el-tab-pane name="scheduled">
				<span slot="label">定时任务 <el-badge :value="tabCount.scheduled" :hidden="tabCount.scheduled === 0" class="tab-badge" type="primary" /></span>
			</el-tab-pane>
		</el-tabs>

		<!-- 表格区域：数字像素高度锚定视口底部，el-table 自管纵横滚动（表头固定、横滚条常驻可视区） -->
		<div class="table-scroll-area" ref="tableArea">
		<el-table ref="taskTable" :data="pagedTasks" border stripe size="small" class="task-table" row-key="taskNo"
			:height="tableHeight"
			v-loading="loading" :row-class-name="taskRowClassName"
			@filter-change="onFilterChange" @sort-change="onSortChange"
			@row-click="onRowClick">
			<el-table-column type="index" label="序号" width="70" align="center" fixed="left" :index="indexMethod"></el-table-column>
			<!-- 预警点列（仅待处理页签）：红=截止已到期，黄=临近截止(一天内)，紫=有反馈要求且已过期未反馈 -->
			<el-table-column v-if="activeTab === 'pending'" label="预警" width="60" align="center">
				<template slot-scope="scope">
					<span v-if="warnLevel(scope.row)" class="warn-dot" :class="'warn-dot--' + warnLevel(scope.row)"></span>
				</template>
			</el-table-column>
			<el-table-column v-for="col in visibleColumns" :key="col.prop" :prop="col.prop" :label="col.label"
				:width="col.width" :min-width="col.minWidth" :sortable="col.sortable ? 'custom' : false"
				:filters="getFilters(col.prop)" :filter-method="genericFilter" :column-key="col.prop" align="center"
				:fixed="col.prop === 'actions' ? 'right' : undefined"
				:render-header="renderHeader">
				<template slot-scope="scope">
					<el-tag v-if="col.prop === 'priority'" :type="priorityTag(scope.row.priority)" size="small">{{ scope.row.priority }}</el-tag>
					<el-tag v-else-if="col.prop === 'status'" :type="statusTag(scope.row.status)" size="small"
						:class="['task-status-tag', 'task-status-tag--' + scope.row.status]">{{ statusMap[scope.row.status] }}</el-tag>
					<el-tag v-else-if="col.prop === 'source'" :type="sourceTag(scope.row.source)" size="small">{{ scope.row.source }}</el-tag>
					<el-tag v-else-if="col.prop === 'docType'" :type="scope.row.docType === '客服工单' ? 'warning' : 'info'" size="small">{{ scope.row.docType }}</el-tag>
				<!-- 关联客户：有客户编码时渲染为下划线链接文字，点击打开客户档案抽屉 -->
				<a v-else-if="col.prop === 'customer' && scope.row.customerCode" href="javascript:;"
					class="customer-link" @click.stop.prevent="goCustomerCard(scope.row)">{{ scope.row.customer || '关联客户' }}</a>
				<span v-else-if="col.prop === 'customer'">{{ scope.row.customer || '—' }}</span>
					<!-- 关联记录：有合并会话记录时显示蓝色链接，可查看完整聊天记录 -->
					<el-button v-else-if="col.prop === 'chatRefCount' && scope.row.chatRefCount > 0"
						type="text" size="small" class="chat-ref-link"
						@click.native.stop="openChatRef(scope.row)">聊天记录({{ scope.row.chatRefCount }})</el-button>
					<span v-else-if="col.prop === 'chatRefCount'" class="action-disabled">—</span>
					<span v-else-if="col.prop === 'points'">{{ scope.row.points }}</span>
					<span v-else-if="col.prop === 'executor'">
						{{ scope.row.executor }}
						<span v-if="scope.row.transferTo && scope.row.transferTo !== scope.row.executor" class="transfer-to"> → {{ scope.row.transferTo }}</span>
					</span>
					<span v-else-if="col.prop === 'actions'" class="action-cell">
						<!-- 定时发起模板：删除 -->
						<template v-if="scope.row.status === 'scheduled' && scope.row.creator === currentNick">
							<el-button type="text" size="small" class="danger-text" @click.native.stop="doDelete(scope.row)">删除</el-button>
						</template>
						<!-- 本人的在办任务：修改 + 处理下拉（含删除） -->
						<template v-else-if="['pending', 'executing', 'shelved'].includes(scope.row.status) && scope.row.mine">
							<el-button type="text" size="small" @click.native.stop="openEditTask(scope.row)">修改</el-button>
							<el-dropdown trigger="click" @command="cmd => doAction(cmd, scope.row)">
							<span class="action-trigger" @click.stop>处理<i class="el-icon-arrow-down el-icon--right"></i></span>
							<el-dropdown-menu slot="dropdown" class="task-action-dropdown">
									<el-dropdown-item v-for="a in actionsOf(scope.row)" :key="a.cmd" :command="a.cmd"
										:divided="!!a.divided" :class="{ 'danger-item': a.cmd === 'discard' }">{{ a.label }}</el-dropdown-item>
									<el-dropdown-item v-if="scope.row.creator === currentNick" divided command="delete"
										class="danger-item">删除</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</template>
						<!-- 已完成：处理过程；本人完成的可回退 -->
						<template v-else-if="scope.row.status === 'completed'">
							<el-button type="text" size="small" @click.native.stop="openProcess(scope.row)">处理过程</el-button>
							<el-button v-if="scope.row.mine" type="text" size="small"
								@click.native.stop="doAction('rollback', scope.row)">回退</el-button>
						</template>
						<!-- 已终止：处理过程；本人终止的可取消终止 -->
						<template v-else-if="scope.row.status === 'discarded'">
							<el-button type="text" size="small" @click.native.stop="openProcess(scope.row)">处理过程</el-button>
							<el-button v-if="scope.row.mine" type="text" size="small"
								@click.native.stop="doAction('cancelDiscard', scope.row)">取消终止</el-button>
						</template>
						<!-- 退回中：接收人申请退回，转办人可在此直接发起审批 -->
						<template v-else-if="scope.row.status === 'returning' && scope.row.pendingReturnId && scope.row.myTransfer && !scope.row.mine">
							<el-button type="text" size="small" @click.native.stop="openReturnReview(scope.row.pendingReturnId)">退回审批</el-button>
							<el-button type="text" size="small" @click.native.stop="openProcess(scope.row)">处理过程</el-button>
						</template>
						<!-- 转办事项-待处理：接收人尚未执行，转办人可取回 -->
						<template v-else-if="scope.row.status === 'pending' && scope.row.myTransfer && !scope.row.mine">
							<el-button type="text" size="small" @click.native.stop="doAction('reclaim', scope.row)">取回</el-button>
							<el-button type="text" size="small" @click.native.stop="openProcess(scope.row)">处理过程</el-button>
						</template>
						<!-- 转办出去仍在途：只读，仅可查看处理过程 -->
						<el-button v-else type="text" size="small" @click.native.stop="openProcess(scope.row)">处理过程</el-button>
					</span>
					<!-- 任务名称：有附件时显示回形针入口，点击查看/下载附件 -->
					<span v-else-if="col.prop === 'name'" class="name-cell">
						<span>{{ scope.row.name }}</span>
						<el-button v-if="scope.row.attachmentCount > 0" type="text" size="small"
							class="attach-link" @click.native.stop="openAttachments(scope.row)"
							title="查看附件">
							<i class="el-icon-paperclip"></i>{{ scope.row.attachmentCount }}
						</el-button>
					</span>
					<span v-else>{{ scope.row[col.prop] }}</span>
				</template>
			</el-table-column>
			<!-- 转办事项页签：最新进度列（接收人最近一次反馈） -->
			<el-table-column v-if="activeTab === 'transferred'" key="latestFeedback" prop="latestFeedback"
				label="最新进度" width="260" align="center">
				<template slot-scope="scope">
					<div v-if="scope.row.latestFeedback" class="feedback-cell">
						<div class="feedback-text">{{ scope.row.latestFeedback }}</div>
						<div class="feedback-time">{{ scope.row.latestFeedbackTime }}</div>
					</div>
					<span v-else class="action-disabled">—</span>
				</template>
			</el-table-column>
		</el-table>

		<!-- 已完成页签：分页（每页行数可自定义） -->
		<div class="pagination-wrapper" v-if="activeTab === 'completed'">
			<el-pagination background @size-change="onSizeChange" @current-change="onPageChange"
				:current-page.sync="currentPage" :page-size="pageSize" :page-sizes="[5, 10, 20, 50]"
				layout="total, sizes, prev, pager, next, jumper" :total="filteredTasks.length">
			</el-pagination>
		</div>
		</div><!-- /table-scroll-area -->

		<!-- 新建/修改任务抽屉与配套弹窗：已抽离为共享组件，"我的任务"与IM转办事项页签共用同一文件 -->
		<task-edit-drawer ref="taskDrawer" :task-list="tasks" @refresh="loadTasks"></task-edit-drawer>
	</div>
</template>

<script>
import http from '../api/httpRequest.js'
import eventBus from '../api/eventBus.js'
import TaskEditDrawer from '../components/task/TaskEditDrawer.vue'

// 固定筛选项（保持引用稳定，避免表格反复更新）
const PRIORITY_FILTERS = [
	{ text: '高', value: '高' },
	{ text: '中', value: '中' },
	{ text: '低', value: '低' }
]
const SOURCE_FILTERS = [
	{ text: '企微转入', value: '企微转入' },
	{ text: '线下指派', value: '线下指派' },
	{ text: 'IM转办', value: 'IM转办' },
	{ text: '无来源', value: '无来源' }
]
// 单据类别筛选：客服工单与普通任务单
const DOC_TYPE_FILTERS = [
	{ text: '客服工单', value: '客服工单' },
	{ text: '任务单', value: '任务单' }
]
const STATUS_FILTERS = [
	{ text: '待处理', value: 'pending' },
	{ text: '执行中', value: 'executing' },
	{ text: '已搁置', value: 'shelved' },
	{ text: '退回中', value: 'returning' },
	{ text: '已转办', value: 'transferred' },
	{ text: '已完成', value: 'completed' },
	{ text: '已终止', value: 'discarded' },
	{ text: '定时发起', value: 'scheduled' }
]

// 列默认顺序（恢复默认时使用）
const DEFAULT_COLUMNS = [
	{ prop: 'taskNo', label: '任务号', width: 150, sortable: true },
	{ prop: 'docType', label: '单据类别', width: 110 },
	{ prop: 'name', label: '任务名称', width: 300 },
	{ prop: 'priority', label: '优先级', width: 100, sortable: true },
	{ prop: 'status', label: '状态', width: 110 },
	{ prop: 'executor', label: '执行人', width: 180 },
	{ prop: 'creator', label: '发起人', width: 120 },
	{ prop: 'source', label: '来源', width: 110 },
	{ prop: 'taskType', label: '任务类型', width: 110 },
	{ prop: 'chatRefCount', label: '关联记录', width: 110 },
	{ prop: 'points', label: '积分', width: 90 },
	{ prop: 'customer', label: '关联客户', width: 140 },
	{ prop: 'deadline', label: '截止时间', width: 180, sortable: true },
	{ prop: 'actions', label: '操作', width: 170 }
];

export default {
	name: 'MyTask',
	components: { TaskEditDrawer },
	created() {
		this.loadViewUsers();
		this.loadTasks();
	},
	mounted() {
		this.$nextTick(this.calcTableHeight);
		window.addEventListener('resize', this.calcTableHeight);
		// 侧边栏/面板展开收起会改变内容区尺寸，监听容器尺寸变化重算
		if (window.ResizeObserver && this.$refs.tableArea) {
			this._ro = new ResizeObserver(() => this.calcTableHeight());
			this._ro.observe(this.$refs.tableArea);
		}
		// 喇叭消息触发：刷新列表 / 打开延期审批 / 打开退回审批
		eventBus.$on('task:refresh', this.loadTasks);
		eventBus.$on('task:openExtensionReview', this.openExtReview);
		eventBus.$on('task:openReturnReview', this.openReturnReview);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.calcTableHeight);
		if (this._ro) { this._ro.disconnect(); this._ro = null; }
		eventBus.$off('task:refresh', this.loadTasks);
		eventBus.$off('task:openExtensionReview', this.openExtReview);
		eventBus.$off('task:openReturnReview', this.openReturnReview);
	},
	data() {
		return {
			activeTab: 'pending',
			// el-table 数字像素高度：由表格顶到视口底部动态算得，保证横滚条常驻可视区
			tableHeight: 300,
			searchKey: '',
			// 按关联客户查询关键字（名称/编码，后端筛选）
			customerKey: '',
			currentPage: 1,
			pageSize: 10,
			// 深拷贝默认列配置
			columns: DEFAULT_COLUMNS.map(c => ({ ...c })),
			dragProp: '',
			// 表头漏斗筛选条件（数据层过滤，先筛后分页）
			activeFilters: {},
			// 数据层排序（先排后分页）
			sortProp: '',
			sortOrder: '',
			statusMap: {
					pending: '待处理',
					executing: '执行中',
					shelved: '已搁置',
					returning: '退回中',
					transferred: '已转办',
					completed: '已完成',
					discarded: '已终止',
					scheduled: '定时发起'
				},
			// 任务数据（从后端 MyWork_Data.my_task 加载，已按登录人过滤）
			tasks: [],
			loading: false,
			// 查看切换：被查看人昵称（空=本人视角；非空=仅查看本部门及下级人员的任务）
			viewUser: '',
			viewUsers: []
		}
	},
computed: {
		// 可见列（保持 columns 数组中的顺序）
		visibleColumns() {
			// 查看他人任务时仅查看：隐藏操作列
			const cols = this.columns.filter(c => this.visibleProps.includes(c.prop));
			if (this.viewUser) return cols.filter(c => c.prop !== 'actions');
			return cols;
		},
		// 可切换查看的人员（有效昵称非空；后端已限定本部门及下级部门在职人员）
		viewUserOptions() {
			return (this.viewUsers || []).filter(u => u.nickName);
		},
		// 列显示/隐藏双向绑定
		visibleProps: {
			get() {
				return this.columns.filter(c => c.visible !== false).map(c => c.prop);
			},
			set(checked) {
				this.columns.forEach(c => {
					c.visible = checked.includes(c.prop);
				});
			}
		},
		// 当前登录人昵称（转办候选人排除本人等）
		currentNick() {
			return (this.userStore && this.userStore.userInfo && this.userStore.userInfo.nickName) || '';
		},
		tabTasks() {
				// 页签归属全部基于后端返回的 mine / myTransfer 标志位
				const map = {
					// 待处理：本人经办且在办（含本人申请退回中的任务）
					pending: t => t.mine && ['pending', 'executing', 'shelved', 'returning'].includes(t.status),
					// 定时发起：本人创建的定时模板
					scheduled: t => t.status === 'scheduled' && t.creator === this.currentNick,
					// 转办事项：本人曾转办、仍在接收链在途、当前不在本人手上（含接收人申请退回中，待本人审批）
					transferred: t => t.myTransfer && !t.mine && ['pending', 'executing', 'shelved', 'returning'].includes(t.status),
					// 已完成：本人完成 或 本人转办出去后被完成
					completed: t => t.status === 'completed' && (t.mine || t.myTransfer),
					// 已终止：本人终止 或 本人转办出去后被终止
					discarded: t => t.status === 'discarded' && (t.mine || t.myTransfer),
					// 知会我的：别人将我设为知会人的任务（仅查看、可补充说明/发表建议）
					notified: t => !!t.notifiedToMe
				};
				return this.tasks.filter(map[this.activeTab]);
			},
		filteredTasks() {
			let list = this.tabTasks;
			// 表头漏斗筛选（先于分页）
			const filterKeys = Object.keys(this.activeFilters);
			if (filterKeys.length) {
				list = list.filter(t => filterKeys.every(k => this.activeFilters[k].includes(t[k])));
			}
			// 关键词搜索
			const kw = this.searchKey.trim();
			if (kw) {
				list = list.filter(t => t.taskNo.includes(kw) || t.name.includes(kw));
			}
			// 排序（先于分页）
			if (this.sortProp && this.sortOrder) {
				const dir = this.sortOrder === 'ascending' ? 1 : -1;
				const weight = { '高': 3, '中': 2, '低': 1 };
				list = [...list].sort((a, b) => {
					const va = this.sortProp === 'priority' ? weight[a.priority] : a[this.sortProp];
					const vb = this.sortProp === 'priority' ? weight[b.priority] : b[this.sortProp];
					if (va < vb) return -1 * dir;
					if (va > vb) return 1 * dir;
					return 0;
				});
			}
			return list;
		},
		// 仅"已完成"页签分页，其他页签显示全部
		pagedTasks() {
			if (this.activeTab !== 'completed') return this.filteredTasks;
			const start = (this.currentPage - 1) * this.pageSize;
			return this.filteredTasks.slice(start, start + this.pageSize);
		},
		tabCount() {
			const active = t => ['pending', 'executing', 'shelved', 'returning'].includes(t.status);
			return {
				pending: this.tasks.filter(t => t.mine && active(t)).length,
				scheduled: this.tasks.filter(t => t.status === 'scheduled' && t.creator === this.currentNick).length,
				transferred: this.tasks.filter(t => t.myTransfer && !t.mine && active(t)).length,
				completed: this.tasks.filter(t => t.status === 'completed' && (t.mine || t.myTransfer)).length,
				discarded: this.tasks.filter(t => t.status === 'discarded' && (t.mine || t.myTransfer)).length,
				notified: this.tasks.filter(t => t.notifiedToMe && t.status !== 'scheduled').length
			};
		},
		executorFilters() {
			return [...new Set(this.tasks.map(t => t.executor).filter(v => v))].map(v => ({ text: v, value: v }));
		},
		creatorFilters() {
			return [...new Set(this.tasks.map(t => t.creator).filter(v => v))].map(v => ({ text: v, value: v }));
		}
	},
	watch: {
		searchKey() {
			this.currentPage = 1;
		},
		// 页签切换：已完成页签有分页栏需预留高度，重算表格高度
		activeTab() {
			this.$nextTick(this.calcTableHeight);
		},
		// 列显隐/顺序、查看模式变化后重新布局
		visibleColumns() {
			this.$nextTick(() => {
				if (this.$refs.taskTable && this.$refs.taskTable.doLayout) this.$refs.taskTable.doLayout();
			});
		},
		// 筛选后数据变少，当前页可能越界，自动回退
		filteredTasks(list) {
			if (this.activeTab !== 'completed') return;
			const maxPage = Math.max(1, Math.ceil(list.length / this.pageSize));
			if (this.currentPage > maxPage) this.currentPage = maxPage;
		}
	},
	methods: {
		// ============ 表格高度：锚定视口底部，保证横向滚动条常驻可视区 ============
		calcTableHeight() {
			const area = this.$refs.tableArea;
			if (!area) return;
			const top = area.getBoundingClientRect().top;
			// 已完成页签底部有分页栏需预留，其他页签仅留底部呼吸间距。
			// 横向滚动条在 el-table 内部 body-wrapper 底部，已包含在 tableHeight 内
			const reserve = this.activeTab === 'completed' ? 54 : 8;
			// scheduled 页签和 pending 等一样无分页
			const h = Math.floor(window.innerHeight - top - reserve);
			this.tableHeight = Math.max(160, h);
			this.$nextTick(() => {
				if (this.$refs.taskTable && this.$refs.taskTable.doLayout) {
					this.$refs.taskTable.doLayout();
				}
			});
		},
		// ============ 列排序（数据层排序，兼容分页）============
		onSortChange({ prop, order }) {
			this.sortProp = order ? prop : '';
			this.sortOrder = order || '';
			this.currentPage = 1;
		},
		// ============ 列筛选（表头漏斗图标，实际过滤在数据层处理以兼容分页）============
		genericFilter() {
			return true;
		},
		onFilterChange(filters) {
			this.activeFilters = {};
			Object.keys(filters).forEach(key => {
				const vals = filters[key];
				if (vals && vals.length) this.activeFilters[key] = vals;
			});
			this.currentPage = 1;
		},
		getFilters(prop) {
			if (prop === 'priority') return PRIORITY_FILTERS;
			if (prop === 'source') return SOURCE_FILTERS;
			if (prop === 'docType') return DOC_TYPE_FILTERS;
			if (prop === 'status') return STATUS_FILTERS;
			if (prop === 'executor') return this.executorFilters;
			if (prop === 'creator') return this.creatorFilters;
			return null;
		},
		// ============ 列左右拖动排序（原生 HTML5 拖拽）============
		renderHeader(h, { column }) {
			return h('span', {
				attrs: { draggable: 'true', title: '拖动可调整列顺序' },
				'class': 'draggable-header',
				on: {
					dragstart: (e) => {
						this.dragProp = column.property;
						e.dataTransfer.effectAllowed = 'move';
						e.dataTransfer.setData('text/plain', column.property);
					},
					dragover: (e) => {
						e.preventDefault();
						e.dataTransfer.dropEffect = 'move';
					},
					drop: (e) => {
						e.preventDefault();
						this.moveColumn(this.dragProp || e.dataTransfer.getData('text/plain'), column.property);
					}
				}
			}, column.label);
		},
		moveColumn(fromProp, toProp) {
			if (!fromProp || fromProp === toProp) return;
			const fromIdx = this.columns.findIndex(c => c.prop === fromProp);
			const toIdx = this.columns.findIndex(c => c.prop === toProp);
			if (fromIdx < 0 || toIdx < 0) return;
			const moved = this.columns.splice(fromIdx, 1)[0];
			// 删除后目标下标可能前移一位
			const insertIdx = this.columns.findIndex(c => c.prop === toProp);
			this.columns.splice(insertIdx < 0 ? toIdx : insertIdx, 0, moved);
		},
		resetColumns() {
			this.columns = DEFAULT_COLUMNS.map(c => ({ ...c, visible: true }));
		},
		// ============ 页签切换（清除残留筛选、重置分页）============
		onTabChange() {
			this.searchKey = '';
			this.currentPage = 1;
			this.activeFilters = {};
			this.sortProp = '';
			this.sortOrder = '';
			this.$nextTick(() => {
				if (this.$refs.taskTable) {
					this.$refs.taskTable.clearFilter();
					this.$refs.taskTable.clearSort();
				}
			});
		},
		// ============ 分页 ============
		onSizeChange(size) {
			this.pageSize = size;
			this.currentPage = 1;
		},
		onPageChange(page) {
			this.currentPage = page;
		},
		// 跨页序号连续：第二页从 11 开始
		indexMethod(indexInPage) {
			return (this.currentPage - 1) * this.pageSize + indexInPage + 1;
		},
		// ============ 标签样式 ============
		priorityTag(p) {
			return { '高': 'danger', '中': 'warning', '低': 'info' }[p] || 'info';
		},
		sourceTag(s) {
			if (s === '企微转入') return '';
			if (s === '线下指派') return 'warning';
			return 'info';
		},
		statusTag(s) {
				return { pending: 'danger', executing: 'warning', shelved: 'info', returning: '', transferred: '', completed: 'success', discarded: 'info', scheduled: 'primary' }[s];
			},
		// ============ 处理动作：执行 / 转办 / 进度反馈 / 完成 / 搁置 / 终止 ============
		actionsOf(row) {
		const status = row.status;
		const discardItem = { cmd: 'discard', label: '终止', divided: true };
		// 申请延期：任务经转办且本人是当前执行人时可用
		const extApplyItem = { cmd: 'extApply', label: '申请延期' };
		const map = {
			// 待处理：先执行，可转办/搁置/完成/终止
			pending: [
				{ cmd: 'accept', label: '执行' },
				{ cmd: 'transfer', label: '转办' },
				{ cmd: 'shelve', label: '搁置' },
				{ cmd: 'complete', label: '完成' },
				extApplyItem,
				discardItem
			],
			// 执行中：先反馈进度，可完成/搁置/转办/终止
			executing: [
				{ cmd: 'feedback', label: '反馈' },
				{ cmd: 'complete', label: '完成' },
				{ cmd: 'shelve', label: '搁置' },
				{ cmd: 'transfer', label: '转办' },
				extApplyItem,
				discardItem
			],
			// 已搁置：恢复执行/转办/完成/终止
			shelved: [
				{ cmd: 'execute', label: '执行' },
				{ cmd: 'transfer', label: '转办' },
				{ cmd: 'complete', label: '完成' },
				extApplyItem,
				discardItem
			]
		};
		let items = map[status] || [];
		// 未经转办的任务不能申请延期；执行人和发起人同一人也不能申请（自己给自己申延期没意义）
		if (!row.hasTransferer || row.executor === row.creator) {
			items = items.filter(i => i.cmd !== 'extApply');
		}
		// 转办接收的任务可申请退回（须直接转办人审批，同意后任务退回给转办人）
		if (row.transferredToMe && items.length) {
			const ret = { cmd: 'return', label: '退回' };
			const idx = items.findIndex(i => i.cmd === 'extApply');
			if (idx >= 0) {
				items.splice(idx, 0, ret);
			} else {
				items.push(ret);
			}
		}
		return items;
	},
		// ============ 加载任务列表（后端已按登录人过滤）============
		mapTask(t) {
				return {
					id: t.id,
					taskNo: t.taskNo,
					name: t.name,
					priority: t.priority,
					status: t.status,
					// 定时发起模板 executor 为空，显示多执行人（顿号分隔）
					executor: t.executor || (t.executors ? String(t.executors).split(',').join('、') : ''),
					executors: t.executors || '',
					transferTo: t.transferTo || '',
					requireFeedback: t.requireFeedback === 1 || t.requireFeedback === true,
					feedbackFrequency: t.feedbackFrequency || null,
					feedbackUnit: t.feedbackUnit || '',
					transferNote: t.transferNote || '',
					creator: t.creator || '',
					source: t.source || '无来源',
					sourceType: t.sourceType || '',
					// 单据类别：客服工单/任务单（历史工单无 source_type 时按来源兼容）
					docType: (t.sourceType === 'ticket' || (!t.sourceType && ['用户自填', '员工代填'].includes(t.source))) ? '客服工单' : '任务单',
					points: t.points || 0,
					customer: t.customer || '',
					// 客户编码/任务类型/处理方式/关闭控制：关联客户链接与抽屉回显使用
					customerCode: t.customerCode || '',
					taskType: t.taskType || '',
					handleMethod: t.handleMethod || '',
					needCreatorClose: t.needCreatorClose === 1 || t.needCreatorClose === true,
					deadline: t.deadline || '—',
					mine: !!t.mine,
					myTransfer: !!t.myTransfer,
					transferredToMe: !!t.transferredToMe,
					// 知会人：是否被知会该任务 + 知会人清单（处理过程展示/维护）
					notifiedToMe: !!t.notifiedToMe,
					notifyUsers: t.notifyUsers || '',
					feedbackOverdue: !!t.feedbackOverdue,
					latestFeedback: t.latestFeedback || '',
					latestFeedbackTime: t.latestFeedbackTime || '',
					scheduleEnabled: !!t.scheduleEnabled,
					scheduleDay: t.scheduleDay || null,
					scheduleTime: t.scheduleTime || '',
					scheduleDeadlineDay: t.scheduleDeadlineDay || null,
				hasTransferer: !!t.hasTransferer,
				pendingReturnId: t.pendingReturnId || null,
				chatRefCount: t.chatRefCount || 0,
				// 关联表单数量（流程审批转任务），抽屉内据此展示关联表单信息
				formRefCount: t.formRefCount || 0,
				attachmentCount: t.attachmentCount || 0,
				urgeCount: t.urgeCount || 0
			};
				},
		loadTasks() {
			this.loading = true;
			const params = [];
			if (this.viewUser) params.push(`viewUser=${encodeURIComponent(this.viewUser)}`);
			if (this.customerKey && this.customerKey.trim()) {
				params.push(`customer=${encodeURIComponent(this.customerKey.trim())}`);
			}
			const qs = params.length ? `?${params.join('&')}` : '';
			// 主列表已包含定时发起模板（后端 WHERE 含 status='scheduled' AND creator=?）
			http.get(`/task/list${qs}`).then(rows => {
				this.tasks = (rows || []).map(this.mapTask);
			}).catch(() => {}).finally(() => {
				this.loading = false;
			});
		},
		// 按客户查询（回车或清空时触发）
		onCustomerSearch() {
			this.currentPage = 1;
			this.loadTasks();
		},
		// 加载可查看人员（本部门及下级部门在职人员，来自基础设置人员表）
		loadViewUsers() {
			http.get('/task/viewUsers').then(rows => {
				this.viewUsers = rows || [];
			}).catch(() => {});
		},
		// 切换查看人：重置页签/筛选/分页后重新加载
		onViewUserChange(val) {
			this.viewUser = val || '';
			this.searchKey = '';
			this.customerKey = '';
			this.activeFilters = {};
			this.sortProp = '';
			this.sortOrder = '';
			this.currentPage = 1;
			this.loadTasks();
		},
		// 退出查看模式（提示条关闭或下拉清空）
		clearViewUser() {
			this.viewUser = '';
			this.currentPage = 1;
			this.loadTasks();
		},
		// 跳转到任务时效分析页面（通过 eventBus 通知 Home 打开页签）
		openTaskAnalysis() {
			eventBus.$emit('open-tab', { path: '/home/task-analysis', title: '任务时效分析' });
		},
		openExtReview(extensionId) {
		// 延期审批弹窗已抽离到 TaskEditDrawer 共享组件，委托子组件处理
		this.$refs.taskDrawer.openExtReview(extensionId);
	},
		openReturnReview(returnId) {
			// 退回审批弹窗已抽离到 TaskEditDrawer 共享组件，委托子组件处理
			this.$refs.taskDrawer.openReturnReview(returnId);
		},
	// 表格行样式：过期未反馈项红色背景（需求4）
		taskRowClassName({ row }) {
			if (row.feedbackOverdue && this.activeTab === 'pending') {
				return 'feedback-overdue-row';
			}
			return '';
		},
		// 预警点等级：red=截止已到期，purple=有反馈要求且过期未反馈，yellow=临近截止(一天内)，空=无预警
		warnLevel(row) {
			if (row.deadline) {
				const dl = new Date(String(row.deadline).replace(/-/g, '/')).getTime();
				const now = Date.now();
				if (!isNaN(dl)) {
					if (dl <= now) return 'red';
					if (dl - now <= 24 * 3600 * 1000) return 'yellow';
				}
			}
			if (row.feedbackOverdue) return 'purple';
			return '';
		},
		doAction(cmd, row) {
			// 处理动作已抽离到 TaskEditDrawer 共享组件，委托子组件处理（含转办/反馈/搁置/完成/终止/延期等）
			this.$refs.taskDrawer.doAction(cmd, row);
		},
		// ============ 处理过程抽屉 ============
		openProcess(row) {
			// 处理过程抽屉已抽离到 TaskEditDrawer 共享组件，委托子组件处理
			this.$refs.taskDrawer.openProcess(row);
		},
	// 打开关联聊天记录弹窗：后端校验任务相关性后返回合并记录
	openChatRef(row) {
		// 关联聊天记录弹窗已抽离到 TaskEditDrawer 共享组件，委托子组件处理
		this.$refs.taskDrawer.openChatRef(row);
	},
	// 打开附件列表弹窗：查看/下载任务附件
	openAttachments(row) {
		this.$refs.taskDrawer.openAttachments(row);
	},
	// 关联客户链接：push 进入客户档案卡片模式（关闭抽屉后自动 back 回我的任务）；带时间戳强制每次触发 watch
	goCustomerCard(row) {
		if (!row || !row.customerCode) return;
		this.$router.push({ path: '/home/customer-archive', query: { cusCode: row.customerCode, cardOnly: 1, _t: Date.now() } });
	},
	// ============ 行点击：复用新建/修改抽屉（与修改按钮同一个抽屉）============
	onRowClick(row) {
		// 知会我的页签：仅查看处理过程（可补充说明/发表建议），不进入编辑表单
		if (this.activeTab === 'notified' && !row.mine) {
			this.openProcess(row);
			return;
		}
		this.openEditTask(row);
	},
		openCreateDialog(preset) {
			// 新建抽屉已抽离到 TaskEditDrawer 共享组件，委托子组件处理
			this.$refs.taskDrawer.openCreateDialog(preset);
		},
		// 修改任务：回填数据到抽屉（任务号、发起人不可改；转办接收的事项名称和截止时间不可改）
		openEditTask(row) {
			// 修改抽屉已抽离到 TaskEditDrawer 共享组件，委托子组件处理
			this.$refs.taskDrawer.openEditTask(row);
		},
		// ============ 删除任务（执行人==发起人 且 在办状态）============
		doDelete(row) {
			// 删除逻辑已抽离到 TaskEditDrawer 共享组件，委托子组件处理
			this.$refs.taskDrawer.doDelete(row);
		}
	}
}
</script>

<style scoped>
.my-task-page {
	padding: 16px;
	height: 100%;
	background: #f5f7fa;
	overflow: hidden;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}
.page-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 6px;
	flex-shrink: 0;
}
.page-title-group {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}
.page-title {
	font-size: 18px;
	font-weight: bold;
	color: #303133;
}
/* 查看模式提示条 */
.view-user-tip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #7a5cd6;
	background: #f3effc;
	border: 1px solid #d9ccf5;
	border-radius: 12px;
	padding: 2px 10px;
	white-space: nowrap;
}
.view-user-tip b {
	font-weight: 600;
}
.view-user-tip .el-icon-close {
	cursor: pointer;
	color: #909399;
}
.view-user-tip .el-icon-close:hover {
	color: #7a5cd6;
}
/* 查看人员切换下拉 */
.view-user-select {
	width: 170px;
}
.view-user-option {
	display: flex;
	flex-direction: column;
	line-height: 1.4;
}
.view-user-name {
	color: #303133;
}
.view-user-meta {
	font-size: 12px;
	color: #909399;
}
.header-actions {
	display: flex;
	align-items: center;
	gap: 10px;
}
.search-input {
	width: 220px;
}
.customer-input {
	width: 190px;
}
.task-tabs {
	margin-bottom: 4px;
	flex-shrink: 0;
}
.task-tabs ::v-deep .el-tabs__header {
	margin-bottom: 8px;
}
.tab-badge {
	margin-left: 4px;
	margin-top: -2px;
}
/* 表格区域：数字像素高度锚定视口底部，el-table 纵向内部滚，横向走外置独立滚动条 */
.table-scroll-area {
	flex: 1;
	min-height: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}
.task-table {
	background: #fff;
	border-radius: 6px;
	/* 覆盖全局 .el-table{flex:1}：表格高度严格由数字 :height 决定，不被父容器拉伸，
	   否则 body-wrapper 被撑高、横向滚动条被顶到视口外 */
	flex: none;
}
/* 列宽超出时显示明确可见的横向滚动条（系统 overlay 滚动条过细易被误认为没有），
   固定在 el-table 可视区底部 */
.task-table ::v-deep .el-table__body-wrapper {
	scrollbar-width: thin;
	scrollbar-color: #c0c4cc transparent;
}
.task-table ::v-deep .el-table__body-wrapper::-webkit-scrollbar {
	height: 10px;
	width: 10px;
}
.task-table ::v-deep .el-table__body-wrapper::-webkit-scrollbar-track {
	background: #f5f7fa;
}
.task-table ::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb {
	background: #c0c4cc;
	border-radius: 5px;
}
.task-table ::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
	background: #909399;
}
/* 过期未反馈行：红色背景（需求4） */
.task-table ::v-deep .feedback-overdue-row,
.task-table ::v-deep .feedback-overdue-row > td {
	background-color: #fef0f0 !important;
}
.task-table ::v-deep .feedback-overdue-row:hover > td {
	background-color: #fde2e2 !important;
}
.pagination-wrapper {
	display: flex;
	justify-content: flex-end;
	padding: 12px 0 4px;
	flex-shrink: 0;
}
.transfer-to {
	color: #e6a23c;
	font-size: 12px;
}
/* 预警点：红=到期，黄=临近(一天内)，紫=未反馈 */
.warn-dot {
	display: inline-block;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	vertical-align: middle;
}
.warn-dot--red {
	background: #f56c6c;
	box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.2);
}
.warn-dot--yellow {
	background: #e6a23c;
	box-shadow: 0 0 0 2px rgba(230, 162, 60, 0.2);
}
.warn-dot--purple {
	background: #9254de;
	box-shadow: 0 0 0 2px rgba(146, 84, 222, 0.2);
}
.action-trigger {
	color: #409eff;
	cursor: pointer;
	font-size: 13px;
}
/* 操作列按钮强制同行，不换行 */
.action-cell {
	display: flex;
	flex-wrap: nowrap;
	white-space: nowrap;
	align-items: center;
	gap: 4px;
}
.action-cell .el-button + .el-button {
	margin-left: 0;
}
.action-disabled {
	color: #c0c4cc;
}
.danger-text {
	color: #f56c6c !important;
}
.draggable-header {
	cursor: move;
	display: inline-block;
}
.draggable-header:active {
	cursor: grabbing;
}
.col-setting-title {
	font-weight: bold;
	margin-bottom: 8px;
	color: #303133;
}
.col-setting-group {
	display: flex;
	flex-direction: column;
}
.col-setting-tip {
	font-size: 12px;
	color: #909399;
	margin: 6px 0;
}
/* 状态标签背景已统一为实底（见 assets/style/element.scss），白字可读，无需再强制深色字体 */
/* 转办事项-最新进度单元格 */
.feedback-cell {
	line-height: 1.5;
}
.feedback-text {
	color: #303133;
	font-size: 12px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.feedback-time {
	color: #909399;
	font-size: 11px;
}
/* ============ IM会话转任务 ============ */
/* 列表中的关联记录链接 */
.chat-ref-link {
	padding: 0;
	font-size: 12px;
	color: #409eff;
}
.chat-ref-link:hover {
	text-decoration: underline;
}
/* 表格行可点击（打开修改抽屉） */
.task-table ::v-deep .el-table__body tr {
	cursor: pointer;
}
/* 任务名称单元格：附件回形针入口 */
.name-cell {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	max-width: 100%;
}
.name-cell > span:first-child {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.attach-link {
	padding: 0;
	font-size: 12px;
	color: #909399;
	flex-shrink: 0;
}
.attach-link:hover {
	color: #409eff;
}
/* 列表中的关联客户链接：纯文字带下划线，去掉按钮质感 */
.customer-link {
	font-size: 12px;
	color: #409eff;
	text-decoration: underline;
	cursor: pointer;
	line-height: 1.5;
	word-break: break-all;
}
.customer-link:hover {
	color: #66b1ff;
}
</style>

<!-- 抽屉/弹窗全局样式已随共享组件迁至 TaskEditDrawer.vue；此处仅保留本页表格下拉菜单样式 -->
<style>
/* 处理菜单中的"终止"红色警示（下拉挂在 body，用页面级 popper-class 限定作用域） */
.task-action-dropdown .danger-item {
	color: #f56c6c;
}
</style>
